/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DegreePlan_LearnerProgressState as LearnerProgressState } from '__generated__/graphql-types';

import { Button } from '@coursera/cds-core';

import EnrollmentDialog from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog';
import EnrollmentButton from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/EnrollmentButton';
import GoToCourseButton from 'bundles/degree-home/components/v3-dpm/components/GoToCourseButton';
import { useCoursePlanningDialog } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/CoursePlanningDialogContext';
import { TermStatus } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/lib/academicTermTypes';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as SegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';

import _t from 'i18n!nls/degree-home';

type Props = {
  course: SegmentCourse;
  hideEnrollmentOptions: boolean;
  termId?: string;
  termStatus: TermStatus;
};

const styles = {
  container: css`
    margin-top: var(--cds-spacing-100);
    gap: var(--cds-spacing-200);
    display: flex;
  `,
};

const AcademicTermsCourseCardActions: React.VFC<Props> = ({ termId, course, hideEnrollmentOptions, termStatus }) => {
  const { openCourseDialog } = useCoursePlanningDialog();
  const [enrollmentModalIsOpen, setEnrollmentModalIsOpen] = React.useState(false);

  /* only display Enrollment, Move, Remove actions for the course if both are true:
    1. the term is not a past term
    2. the course is a planned course */
  if (termStatus !== TermStatus.COMPLETE && course.learnerProgress.state === LearnerProgressState.Planned) {
    return (
      <React.Fragment>
        <div css={styles.container}>
          {!hideEnrollmentOptions && (
            <EnrollmentButton course={course} onClick={() => setEnrollmentModalIsOpen(true)} />
          )}

          <Button variant="ghost" size="small" onClick={() => openCourseDialog(course, 'move', termId)}>
            {_t('Move')}
          </Button>
          <Button variant="ghost" size="small" onClick={() => openCourseDialog(course, 'remove', termId)}>
            {_t('Remove')}
          </Button>
        </div>
        <EnrollmentDialog
          course={course}
          modalIsOpen={enrollmentModalIsOpen}
          closeModal={() => setEnrollmentModalIsOpen(false)}
        />
      </React.Fragment>
    );
  }

  return (
    <div css={styles.container}>
      <GoToCourseButton course={course} />
    </div>
  );
};

export default AcademicTermsCourseCardActions;
